import type { ComponentType } from 'react';
import { createElement, useCallback, useState } from 'react';

type Props = {
  onClose: () => void;
  open: boolean;
};

export type BaseModalProps = Props;

type Options = {
  keepMounted?: boolean;
  defaultOpen?: boolean;
};

export const useModal = <T extends BaseModalProps = BaseModalProps, K = Omit<T, 'onClose' | 'open'>>(ModalComponent: ComponentType<Props & K>, options: Options = {}) => {
  const [visible, setVisibility] = useState(options.defaultOpen ?? false);

  const toggleVisibility = useCallback(() => {
    setVisibility(s => !s);
  }, []);

  const Modal = useCallback((props: K) => {
    if (!options.keepMounted && !visible) {
      return null;
    }

    // `Props & K` can't be re-derived by TS from `{ onClose, open } & K` when K is an
    // unconstrained generic, and JSX's LibraryManagedAttributes check on a generically-typed
    // component hits the same wall — build the props explicitly and use createElement instead
    // of JSX to sidestep that check.
    const modalProps = {
      onClose: toggleVisibility,
      open: visible,
      ...props,
    } as Props & K;

    return createElement(ModalComponent, modalProps);
  }, [
    ModalComponent,
    options.keepMounted,
    toggleVisibility,
    visible,
  ]);

  return [toggleVisibility, Modal, visible] as const;
};
